import {
  ReactNode,
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Canvas, PencilBrush } from 'fabric';

import {
  BRUSH_THICKNESS_ELEMENT_ID,
  CONTENT_ELEMENT_ID,
  CURSOR_ELEMENT_ID,
  CURSOR_OFFSET,
  CursorProps,
  defaultCursor,
  defaultEraser,
  defaultPencil,
  defaultText,
  EraserProps,
  PencilProps,
  TextProps,
  Tool,
  ToolSet,
} from './draw-canvas.constants';
import { toRgb } from 'lib/strings/strings.colour';
import { PathEraserBrush } from './toolbar/path-eraser-brush';
import { getControlCursor } from 'approot/activity-builder/components/draw/canvas/draw.utils';
import isMobileBrowser from 'approot/shared/logic/is-mobile-browser';
import { isArtboard } from './logic/is-artboard';

type DrawContextType = {
  toolSet: ToolSet | null;
  setToolSet: (toolSet: ToolSet | null) => void;
  strokeColour: string;
  fillColour: string;
  setStrokeColour: (strokeColour: string) => void;
  setFillColour: (fillColour: string) => void;
  setupCustomCursor: (canvas: Canvas, brush: PencilProps) => () => void;
  turnOffDrawMode: (canvas: Canvas) => void;
  setPencilDrawing: (canvas: Canvas, brush: PencilProps) => void;
  pencil: PencilProps;
  setPencil: (pencil: PencilProps) => void;
  eraserBrush: EraserProps;
  setEraserBrush: (eraserBrush: EraserProps) => void;
  setEraserDrawing: (canvas: Canvas, brush: EraserProps) => void;
  textTool: TextProps;
  setTextTool: (textTool: TextProps) => void;
  cursor: CursorProps;
  setCursor: (cursor: CursorProps) => void;
  setCursorDrawing: (canvas: Canvas, cursor: CursorProps) => void;
  rotateCursor: boolean;
  setRotateCursor: (rotateCursor: boolean) => void;
};

const noop = () => {};

export const DrawContext = createContext<DrawContextType>({
  toolSet: null,
  setToolSet: noop,
  setupCustomCursor: () => noop,
  turnOffDrawMode: noop,
  setPencilDrawing: noop,
  pencil: defaultPencil,
  setPencil: noop,
  eraserBrush: defaultEraser,
  setEraserBrush: noop,
  setEraserDrawing: noop,
  textTool: defaultText,
  setTextTool: noop,
  cursor: defaultCursor,
  setCursor: noop,
  setCursorDrawing: noop,
  strokeColour: '',
  fillColour: '',
  setStrokeColour: noop,
  setFillColour: noop,
  rotateCursor: false,
  setRotateCursor: noop,
});

export const DrawContextProvider = ({ children }: { children: ReactNode }) => {
  // current selected tool
  const [toolSet, setToolSet] = useState<ToolSet | null>(null);
  const [pencil, setPencil] = useState<PencilProps>(defaultPencil);
  const [eraserBrush, setEraserBrush] = useState<EraserProps>(defaultEraser);
  const [textTool, setTextTool] = useState<TextProps>(defaultText);
  const [cursor, setCursor] = useState<CursorProps>(defaultCursor);
  const [strokeColour, setStrokeColour] = useState<string>('');
  const [fillColour, setFillColour] = useState<string>('');
  const [rotateCursor, setRotateCursor] = useState<boolean>(false);
  // Refs to hold the latest values
  const toolSetRef = useRef<ToolSet | null>(toolSet);
  const pencilRef = useRef<PencilProps>(pencil);
  const eraserBrushRef = useRef<EraserProps>(eraserBrush);
  const cursorRef = useRef<CursorProps>(cursor);

  const setupCustomCursor = useCallback((canvas: Canvas) => {
    // Don't setup custom cursors on mobile devices
    if (isMobileBrowser()) {
      return noop;
    }

    const cursorEl = document.getElementById(CURSOR_ELEMENT_ID);
    const contentEl = document.getElementById(CONTENT_ELEMENT_ID);
    const brushThicknessEl = document.getElementById(
      BRUSH_THICKNESS_ELEMENT_ID
    );

    if (!contentEl || !cursorEl || !brushThicknessEl) return noop;

    function hideDefaultCursor() {
      canvas.setCursor('none');
      canvas.freeDrawingCursor = 'none';
      canvas.forEachObject(function (object) {
        object.hoverCursor = 'none';
      });
    }

    const updateCursor = (evt: MouseEvent) => {
      const isCursorBrush =
        toolSetRef.current?.tool === Tool.Pencil ||
        toolSetRef.current?.tool === Tool.Eraser;

      const tool = toolSetRef.current?.tool;
      const brush =
        tool === Tool.Pencil ? pencilRef.current : eraserBrushRef.current;

      const zoom = canvas.getZoom();
      const size = (brush.size < 6 ? 6 : brush.size) * zoom;

      cursorEl.style.position = 'fixed';
      cursorEl.style.zIndex = '9999';
      cursorEl.style.pointerEvents = 'none';

      // We want to hide the native cursor when using a cursor image
      hideDefaultCursor();

      // Styles for both brush and non-brush cursor types
      cursorEl.style.display = 'block';
      cursorEl.style.width = `${defaultCursor.size}px`;
      cursorEl.style.height = `${defaultCursor.size}px`;

      // cleanup non image cursor styles
      cursorEl.style.border = '';
      cursorEl.style.borderRadius = '';

      if (isCursorBrush) {
        // Seperate div to indicate brush thickness
        brushThicknessEl.style.position = 'fixed';
        brushThicknessEl.style.zIndex = '9998';
        brushThicknessEl.style.pointerEvents = 'none';
        brushThicknessEl.style.width = `${size}px`;
        brushThicknessEl.style.height = `${size}px`;
        brushThicknessEl.style.border = `2px solid ${brush.colour}`;
        brushThicknessEl.style.borderRadius = '50%';
        brushThicknessEl.style.left = `${evt.x - size / 2}px`;
        brushThicknessEl.style.top = `${evt.y - size / 2}px`;

        // Position the cursor on the top right corner of the brush thickness
        cursorEl.style.left = `${evt.x}px`;
        cursorEl.style.top = `${evt.y - CURSOR_OFFSET}px`;
      } else {
        cursorEl.style.left = `${evt.x - defaultCursor.size / 15}px`;
        cursorEl.style.top = `${evt.y - defaultCursor.size / 15}px`;

        // cleanup non cursor brush styles
        brushThicknessEl.style.border = 'none';
        brushThicknessEl.style.borderRadius = '';
      }

      if ((evt.target as HTMLElement).classList.contains('upper-canvas')) {
        cursorEl.style.display = 'block';
        brushThicknessEl.style.display = 'block';
      } else {
        cursorEl.style.display = 'none';
        brushThicknessEl.style.display = 'none';
      }

      const selectedElement = canvas.getActiveObject();

      // We use the native cursor when the user is resizing a shape
      if (selectedElement?.__corner) {
        if (selectedElement.__corner === 'mtr') {
          // We use a rotate cursor var as all other cursors rely on a tool
          setRotateCursor(true);
        } else {
          hideCustomCursor();
          const controlCursor = getControlCursor(selectedElement.__corner);

          canvas.setCursor(controlCursor);
          canvas.freeDrawingCursor = controlCursor;
        }
      } else {
        setRotateCursor(false);
      }
    };

    const hideCustomCursor = () => {
      if (!cursorEl) {
        return;
      }

      cursorEl.style.display = 'none';
      brushThicknessEl.style.display = 'none';
    };

    const turnOffCanvasCursor = () => {
      if (!cursorEl) {
        return;
      }

      canvas.setCursor('none');
      canvas.freeDrawingCursor = 'none';
      canvas.hoverCursor = 'none';
      canvas.defaultCursor = 'none';
    };

    contentEl.addEventListener('mousemove', updateCursor);
    contentEl.addEventListener('mouseleave', hideCustomCursor);
    contentEl.addEventListener('mouseenter', turnOffCanvasCursor);

    return () => {
      contentEl.removeEventListener('mousemove', updateCursor);
      contentEl.removeEventListener('mouseleave', hideCustomCursor);
      contentEl.removeEventListener('mouseenter', turnOffCanvasCursor);
    };
  }, []);

  const turnOffDrawMode = useCallback(
    (canvas: Canvas, disableSelection?: boolean) => {
      canvas.isDrawingMode = false;
      const cursorEl = document.getElementById('custom-cursor');
      if (cursorEl && toolSetRef.current?.tool !== Tool.Cursor) {
        cursorEl.style.display = 'none';
      }

      if (disableSelection) {
        canvas.forEachObject(function (object) {
          object.selectable = false;
        });
      } else {
        canvas.forEachObject(function (object) {
          object.selectable = object.type !== 'image' && !isArtboard(object);
        });
      }
    },
    []
  );

  const setPencilDrawing = useCallback(
    (canvas: Canvas, pencil: PencilProps) => {
      canvas.freeDrawingBrush = new PencilBrush(canvas);
      canvas.freeDrawingBrush.width = pencil.size;
      canvas.freeDrawingBrush.color = toRgb(pencil.colour);
      canvas.isDrawingMode = true;

      setToolSet({ tool: Tool.Pencil });
      setPencil(pencil);
    },
    [setPencil, setToolSet]
  );

  const setEraserDrawing = useCallback(
    (canvas: Canvas, brush: EraserProps) => {
      canvas.freeDrawingBrush = new PathEraserBrush(canvas);
      canvas.freeDrawingBrush.width = brush.size;
      canvas.freeDrawingBrush.color = toRgb(brush.colour);
      canvas.isDrawingMode = true;

      setToolSet({ tool: Tool.Eraser });
      setEraserBrush(brush);
    },
    [setEraserBrush, setToolSet]
  );

  const setCursorDrawing = useCallback(
    (canvas: Canvas, cursor: CursorProps) => {
      setToolSet({ tool: Tool.Cursor });
      canvas.discardActiveObject();
      canvas.renderAll();
      turnOffDrawMode(canvas, true);
      setCursor(cursor);
    },
    [setCursor, setToolSet, turnOffDrawMode]
  );

  // Update refs whenever state changes
  useEffect(() => {
    toolSetRef.current = toolSet;
    pencilRef.current = pencil;
    eraserBrushRef.current = eraserBrush;
    cursorRef.current = cursor;
  }, [toolSet, pencil, eraserBrush, cursor]);

  const value = useMemo(
    () => ({
      toolSet,
      setToolSet,
      setupCustomCursor,
      turnOffDrawMode,
      setPencilDrawing,
      setEraserDrawing,
      pencil,
      setPencil,
      eraserBrush,
      setEraserBrush,
      textTool,
      setTextTool,
      cursor,
      setCursor,
      setCursorDrawing,
      strokeColour,
      fillColour,
      setStrokeColour,
      setFillColour,
      rotateCursor,
      setRotateCursor,
    }),
    [
      toolSet,
      pencil,
      setPencil,
      setPencilDrawing,
      setupCustomCursor,
      turnOffDrawMode,
      eraserBrush,
      setEraserBrush,
      setEraserDrawing,
      textTool,
      setTextTool,
      cursor,
      setCursor,
      setCursorDrawing,
      strokeColour,
      fillColour,
      setStrokeColour,
      setFillColour,
      rotateCursor,
      setRotateCursor,
    ]
  );

  return <DrawContext.Provider value={value}>{children}</DrawContext.Provider>;
};

export const useDrawCanvasContext = () => useContext(DrawContext);
